import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Trash2, RefreshCw, CheckCircle, ArrowLeft, Eye, Edit } from 'lucide-react';

const PreApplicationManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pre-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load pre-applications');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this pre-application?')) return;
    
    try {
      await axios.delete(`${API}/pre-applications/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pre-application deleted successfully');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to delete pre-application');
      console.error(error);
    }
  };

  const viewApplication = (application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  };

  const editApplication = (application) => {
    navigate('/field/pre-application', { state: { editData: application } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Button onClick={() => navigate('/admin')} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <FileText className="h-6 w-6 text-gray-600" />
          <h2 className="text-2xl font-bold">Pre-Application Management</h2>
        </div>
        <Button onClick={fetchApplications} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{applications.length}</p>
                <p className="text-sm text-gray-500">Total Applications</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{applications.filter(a => a.us_work_authorized).length}</p>
                <p className="text-sm text-gray-500">Work Authorized</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{applications.filter(a => a.has_drivers_license).length}</p>
                <p className="text-sm text-gray-500">Has Driver's License</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {applications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No pre-applications found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{app.full_name}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Position:</strong> {app.position_applied_for}</span>
                      <span>•</span>
                      <span><strong>Email:</strong> {app.email}</span>
                      <span>•</span>
                      <span><strong>Phone:</strong> {app.phone}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {app.us_work_authorized && (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Work Auth
                      </Badge>
                    )}
                    {app.has_drivers_license && (
                      <Badge className="bg-blue-100 text-blue-800">
                        DL
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-700"><strong>Address:</strong></p>
                      <p className="text-sm text-gray-600">{app.address}</p>
                      <p className="text-sm text-gray-600">{app.city}, {app.state} {app.zip_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700"><strong>Date of Birth:</strong></p>
                      <p className="text-sm text-gray-600">{_fmtDate(app.date_of_birth)}</p>
                      {app.ssn_last_four && (
                        <>
                          <p className="text-sm text-gray-700 mt-2"><strong>SSN (Last 4):</strong></p>
                          <p className="text-sm text-gray-600">***-**-{app.ssn_last_four}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {app.employment_history && app.employment_history.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700 mb-2"><strong>Employment History ({app.employment_history.length}):</strong></p>
                    {app.employment_history.slice(0, 2).map((emp, idx) => (
                      <div key={idx} className="mb-2 last:mb-0">
                        <p className="text-sm text-gray-600">
                          <strong>{emp.employer_name}</strong> - {emp.position}
                        </p>
                        <p className="text-xs text-gray-500">
                          {emp.start_date} to {emp.end_date || 'Present'}
                        </p>
                      </div>
                    ))}
                    {app.employment_history.length > 2 && (
                      <p className="text-xs text-gray-500 mt-1">+ {app.employment_history.length - 2} more</p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-xs text-gray-500">
                    <span>Submitted: {_fmtDateTime(app.submitted_at)}</span>
                    {app.references && app.references.length > 0 && (
                      <span className="ml-4">📋 {app.references.length} references</span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewApplication(app);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        editApplication(app);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteApplication(app.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pre-Application Details</DialogTitle>
          </DialogHeader>
          {selectedApplication && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <p className="text-sm font-semibold text-gray-600">Full Name</p>
                  <p className="text-base">{selectedApplication.full_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Email</p>
                  <p className="text-base">{selectedApplication.email}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Phone</p>
                  <p className="text-base">{selectedApplication.phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-semibold text-gray-600">Address</p>
                  <p className="text-base">{selectedApplication.address}</p>
                  <p className="text-base">{selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Date of Birth</p>
                  <p className="text-base">{_fmtDate(selectedApplication.date_of_birth)}</p>
                </div>
                {selectedApplication.ssn_last_four && (
                  <div>
                    <p className="text-sm font-semibold text-gray-600">SSN (Last 4)</p>
                    <p className="text-base">***-**-{selectedApplication.ssn_last_four}</p>
                  </div>
                )}
              </div>

              {selectedApplication.employment_history && selectedApplication.employment_history.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Employment History ({selectedApplication.employment_history.length})</p>
                  <div className="space-y-3">
                    {selectedApplication.employment_history.map((emp, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded">
                        <p className="font-medium">{emp.employer_name}</p>
                        <p className="text-sm text-gray-600">{emp.position}</p>
                        <p className="text-xs text-gray-500">{emp.start_date} to {emp.end_date || 'Present'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedApplication.references && selectedApplication.references.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">References ({selectedApplication.references.length})</p>
                  <div className="space-y-2">
                    {selectedApplication.references.map((ref, idx) => (
                      <div key={idx} className="p-3 border rounded">
                        <p className="font-medium">{ref.name}</p>
                        <p className="text-sm text-gray-600">{ref.relationship}</p>
                        <p className="text-sm text-gray-600">{ref.phone}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedApplication.signature && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Signature</p>
                  <img src={selectedApplication.signature} alt="Signature" className="border rounded p-2" style={{maxHeight: '100px'}} />
                </div>
              )}

              <div className="text-sm text-gray-500 pt-4 border-t">
                Submitted: {_fmtDateTime(selectedApplication.submitted_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreApplicationManagement;
